<template>
  <div class="qr-contents">
    <div class="qr-contents-close">
      <RouterLink :to="{ name: 'ApplicationsList' }">
        <FeatherIcon icon="x" stroke="white" />
      </RouterLink>
    </div>

    <div class="qr-contents-top">
      <p class="text-size-18 text-weight-500 text-white">旧出退勤リーダー</p>
      <p class="mt-2 text-size-14 text-white">
        スキャンするにはQRコードをフレーム内に合わせてください
      </p>
    </div>

    <div class="qr-contents-middle">
      <div :class="qrContentsFrameClass"></div>
    </div>

    <div class="qr-contents-bottom">
      <p class="text-size-14 text-white">
        QRコードが読み取れない場合は
        <RouterLink
          class="link"
          :to="{
            name: 'QrScannerWeb',
            query: $route.query
          }"
          >新しい出退勤リーダー</RouterLink
        >
        を使うか、事業所担当者に4桁のコードを確認して以下に入力してください
      </p>

      <AttendanceCodeForm :workStatus="workStatus" @submit="submit" />
    </div>
    <div></div>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-facing-decorator";
import { AbTest3300, AbTest3401 } from "@/app/services/AbTestService";
import { applicationService } from "@/app/services/ApplicationService";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import { startScan, stopScan } from "@/utils/qrCodeScanner";
import { showErrorModal } from "@/utils/showErrorModal";
import { deviceModule } from "@/store/device";
import { applicationStore } from "@/store/application";
import { systemService } from "@/app/services/SystemService";
import AttendanceCodeForm from "@/components/organisms/orders/AttendanceCodeForm.vue";
import { trainingService } from "@/app/services/TrainingService";

@Component({
  components: {
    FeatherIcon,
    AttendanceCodeForm
  }
})
export default class QrScanner extends Vue {
  officeCode = "";
  // Androidのみ管理される変数
  // trueの場合（inputにfocusが当たっている場合）inputを画面上部に持っていく
  isFocusInput = false;
  get applicationId() {
    return Number(this.$route.query.id);
  }

  get workStatus() {
    return this.$route.query.workStatus;
  }

  get isOvertime() {
    return this.$route.query.isOvertime === "true";
  }

  get isBrowser() {
    return deviceModule.isBrowser;
  }

  get qrContentsFrameClass() {
    return this.isFocusInput
      ? "qr-contents__frame__focus-input"
      : "qr-contents__frame";
  }

  mounted() {
    const query = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      route_name: (this.$route.name as string) || undefined,
      event: "view"
    };
    systemService.log(query);
    // QRスキャナーを起動
    this.startScan();
  }

  beforeUnmount() {
    this.stop();
  }

  async startScan() {
    const officeCode = await startScan();
    const isSuccess = await this.submit(officeCode, "qrcode");
    if (isSuccess) return;
    await this.startScan();
  }

  stop() {
    stopScan();
  }

  async submit(
    officeCode: string,
    method: "qrcode" | "manual"
  ): Promise<boolean> {
    const query = {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      route_name: (this.$route.name as string) || undefined,
      event: "submit",
      workStatus: this.workStatus,
      officeCode,
      method
    };
    systemService.log(query);

    try {
      this.$loading.show();
      const isStartWork = this.workStatus === "start";
      if (isStartWork) {
        const trainingId = await trainingService.searchNotFinishedTraining(
          this.applicationId
        );
        await applicationService.startWork(this.applicationId, officeCode);
        if (trainingId) {
          // トレーニングがある場合はトレーニングページに遷移
          await this.$router.replace({
            name: "TrainingDetail",
            params: { trainingId },
            query: { applicationId: this.applicationId.toString() }
          });
          return true;
        }
      } else {
        await applicationService.finishWork(
          this.applicationId,
          officeCode,
          this.isOvertime
        );
        AbTest3401.registerEvent(AbTest3401.eventTypes.completeWork);
        AbTest3300.registerEvent(AbTest3300.eventTypes.completeWork);
      }
      await applicationStore.fetchApplications();
      await this.$router.replace({
        name: this.workStatus === "start" ? "WorkStarted" : "WorkFinished",
        params: { applicationId: this.applicationId.toString() }
      });
      return true;
    } catch (error) {
      await showErrorModal(this, error as Error);
    } finally {
      this.$loading.hide();
    }
    return false;
  }

  onFocus() {
    // androidの場合のみ
    if (deviceModule.isAndroid) {
      this.stop();
      this.isFocusInput = true;
    }
  }

  onBlur() {
    // androidの場合のみ
    if (deviceModule.isAndroid) {
      this.startScan();
      this.isFocusInput = false;
    }
  }
}
</script>

<style lang="scss" scoped>
.qr-contents {
  position: fixed;
  display: grid;
  grid-template-areas:
    "left top right"
    "left middle right"
    "left bottom right";
  grid-template-rows: auto auto 1fr;
  grid-template-columns: 1fr 240px 1fr;
  width: 100%;
  height: 100vh;
  text-align: center;

  &::before {
    grid-area: left;
    content: "";
    background-color: rgba($black, 0.6);
  }

  &::after {
    grid-area: right;
    content: "";
    background-color: rgba($black, 0.6);
  }

  &-close {
    position: absolute;
    top: calc(env(safe-area-inset-top, 0px) + 10px);
    right: 16px;
  }

  &-top {
    grid-area: top;
    padding-top: 60px;
    padding-bottom: 30px;
    background-color: rgba($black, 0.6);
  }

  &-middle {
    grid-area: middle;
    overflow: hidden;
  }

  &-frame {
    height: 260px;
    margin: -10px;
    border: 10px solid rgba($black, 0.6);
    border-radius: 20px;
    transition: all 0.2s;

    &-focus-input {
      height: 0;
      background-color: rgba($black, 0.6);
      transition: all 0.2s;
    }
  }

  &-bottom {
    grid-area: bottom;
    padding-top: 30px;
    background-color: rgba($black, 0.6);
  }

  .link {
    color: $primary-yellow;
    text-decoration: underline;
  }
}
</style>
